import { useState } from 'react';
import { LoginScreen } from './auth/LoginScreen';
import { VerifyScreen } from './auth/VerifyScreen';
import { RegisterScreen } from './auth/RegisterScreen';

interface AuthFlowProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; memberId?: string }) => void;
  initialScreen?: 'login' | 'verify' | 'register';
  magicToken?: string;
  magicContact?: string;
}

type AuthScreen = 'login' | 'verify' | 'register';

export function AuthFlow({ onNavigate, initialScreen = 'login', magicToken, magicContact }: AuthFlowProps) {
  const [currentScreen, setCurrentScreen] = useState<AuthScreen>(initialScreen);
  const [contact, setContact] = useState<string>(magicContact || '');
  const [memberId, setMemberId] = useState<string | undefined>(undefined);
  const token = magicToken || '';

  const handleLoginSuccess = (userContact: string) => {
    setContact(userContact);
  };

  const handleNeedRegistration = (userContact: string, memberIdFromMagicLink?: string) => {
    setContact(userContact);
    setMemberId(memberIdFromMagicLink);
    setCurrentScreen('register');
  };

  const handleRegistrationComplete = () => {
    // Set flag that user just authenticated (used by invitation auto-accept)
    sessionStorage.setItem('just_authenticated', 'true');
    
    // Check if there's a redirect path from magic link
    const authRedirect = sessionStorage.getItem('auth_redirect');
    if (authRedirect) {
      sessionStorage.removeItem('auth_redirect');
      
      // Navigate based on the redirect path
      if (authRedirect.startsWith('/invitation/')) {
        // Extract memberId from the path
        const memberId = authRedirect.split('/invitation/')[1];
        if (memberId) {
          // Store in localStorage as backup
          localStorage.setItem('pending_invitation_accept', memberId);
          // Pass directly to avoid race condition
          onNavigate('gift-group-acceptance', { memberId });
          return;
        }
      }
    }
    
    // Check if there's a pending gift group to create
    const pendingGiftGroup = localStorage.getItem('pendingGiftGroup');
    if (pendingGiftGroup) {
      // Navigate to create-gift-group to complete the creation
      onNavigate('create-gift-group');
    } else {
      // After registration, go directly to dashboard
      onNavigate('dashboard');
    }
  };

  const handleAuthComplete = () => {
    // Set flag that user just authenticated (used by invitation auto-accept)
    sessionStorage.setItem('just_authenticated', 'true');
    
    // Check if there's a redirect path from magic link
    const authRedirect = sessionStorage.getItem('auth_redirect');
    if (authRedirect) {
      sessionStorage.removeItem('auth_redirect');
      
      // Navigate based on the redirect path
      if (authRedirect.startsWith('/invitation/')) {
        // Extract memberId from the path
        const memberId = authRedirect.split('/invitation/')[1];
        if (memberId) {
          // Store in localStorage as backup
          localStorage.setItem('pending_invitation_accept', memberId);
          // Pass directly to avoid race condition
          onNavigate('gift-group-acceptance', { memberId });
          return;
        }
      }
    }
    
    // Check if there's a pending gift group to create
    const pendingGiftGroup = localStorage.getItem('pendingGiftGroup');
    if (pendingGiftGroup) {
      // Navigate to create-gift-group to complete the creation
      onNavigate('create-gift-group');
    } else {
      onNavigate('dashboard');
    }
  };

  return (
    <div>
      {currentScreen === 'login' && (
        <LoginScreen
          onSuccess={handleLoginSuccess}
          onNavigate={onNavigate}
        />
      )}

      {currentScreen === 'verify' && (
        <VerifyScreen
          token={token}
          contact={contact}
          onSuccess={handleAuthComplete}
          onNeedRegistration={handleNeedRegistration}
          onBack={() => setCurrentScreen('login')}
        />
      )}

      {currentScreen === 'register' && (
        <RegisterScreen
          contact={contact}
          memberId={memberId}
          onSuccess={handleRegistrationComplete}
        />
      )}
    </div>
  );
}
